import type { BaseEvent } from '@amplitude/analytics-types';

import { BuildType } from 'src/enums/build/build-type';
import { JourneyType } from 'src/enums/journey-type';
import type { Language } from 'src/enums/language';
import type { PAGE_LEVEL } from 'src/enums/page-level';
import { TourType } from 'src/enums/tour-type';
import { isOnAdminPath } from 'src/helpers/admin-path';
import { isJumpLink } from 'src/helpers/link-helper';
import { pathToTitle } from 'src/helpers/string-helper';

export const amplitudeBrandName = (buildType: BuildType) => {
  switch (buildType) {
    case BuildType.MERIDIAN:
      return 'Meridian';
    case BuildType.HARBOUR:
      return 'Harbour';
    default:
      return 'David Lloyd';
  }
};

const amplitudeCTAEventName = 'Click to CTA';
const amplitudeLinkEventName = 'Click to Link';
export const amplitudeLogoClickEvent = 'Click to Logo';

type CTAEventProperties = { ctaName: string | null | undefined; component: string } & Record<
  string,
  any
>;
type LinkEventProperties = { linkName: string | null | undefined; component: string } & Record<
  string,
  any
>;
type SelectEventProperties = { select: string | null; component: string } & Record<string, any>;
type ViewEventProperties = { view: string; component: string } & Record<string, any>;
type FormEventProperties = { formName: string } & Record<string, any>;

export type VideoAndAccordionAmplitudeProps = {
  eventInput: string;
  componentName: string;
  ctaEventName: string;
  ctaName: string;
  playButtonText?: string;
};

type AmplitudeEventProperties = { buildType: BuildType; language: Language; brand: string } & (
  | LinkEventProperties
  | CTAEventProperties
  | SelectEventProperties
  | ViewEventProperties
  | FormEventProperties
  | undefined
);

export const amplitudeLinkTrackEvent = ({
  buildType,
  language,
  linkName,
  linkPath,
  componentName,
  pageLevel,
  ...otherProps
}: {
  buildType: BuildType;
  language: Language;
  linkName: string | null | undefined;
  linkPath: string | null;
  componentName: string;
  pageLevel?: PAGE_LEVEL;
} & Record<string, any>) =>
  amplitudeTrackEvent(amplitudeLinkEventName, {
    custom_page: window.location.origin,
    custom_level: pageLevel,
    brand: amplitudeBrandName(buildType),
    buildType,
    language,
    linkName,
    linkPath,
    component: componentName,
    formName: null,
    isJumpLink: isJumpLink(linkPath ?? ''),
    ...otherProps,
  });

export const amplitudeTrackEvent = (
  eventInput: string | BaseEvent,
  eventProperties?: AmplitudeEventProperties
) => {
  if (isOnAdminPath()) {
    return Promise.resolve();
  }

  return (
    import('@amplitude/analytics-browser')
      .then(({ track }) => track(eventInput, eventProperties))
      // eslint-disable-next-line no-console
      .catch(console.error)
  );
};

export const amplitudeButtonTrackEvent = ({
  buildType,
  language,
  button,
  componentName,
  pageLevel,
  ctaName,
  formName = null,
  ...otherProps
}: {
  buildType: BuildType;
  language: Language;
  button: Queries.RedesignButton | null | undefined;
  componentName: string;
  formName?: string | null;
  pageLevel?: PAGE_LEVEL;
  ctaName?: string | null;
} & Record<string, any>) =>
  amplitudeTrackEvent(amplitudeCTAEventName, {
    custom_page: window.location.origin,
    custom_level: pageLevel,
    brand: amplitudeBrandName(buildType),
    buildType,
    language,
    ctaTitle: pathToTitle(button?.buttonLink || ''),
    ctaPath: button?.buttonLink,
    ctaName: ctaName || button?.buttonText,
    component: componentName,
    formName,
    ...otherProps,
  });

export const getTourMessageForAmplitudeEvent = (tourType: TourType) => {
  switch (tourType) {
    case TourType.BOOK_A_TOUR:
      return 'Book a tour';
    case TourType.CLUB_WELCOME:
      return 'Book a club welcome';
    case TourType.TRIAL:
      return 'Trial';
  }
};

export const getJoinMessageForAmplitudeEvent = (journeyType: JourneyType) => {
  switch (journeyType) {
    case JourneyType.NORMAL:
      return 'Join Normal';
    case JourneyType.FRIENDS_AND_FAMILY:
      return 'Join Friends and Family';
    case JourneyType.TDR_CAPITAL:
      return 'Join TDRCapital';
    case JourneyType.CORPORATE:
      return 'Join Corporate';
    case JourneyType.JOIN_IN_CLUB:
      return 'Join In Club';
    case JourneyType.TRIAL:
      return 'Join Trial';
    case JourneyType.YOUNG_ADULT:
      return 'Join Young Adult';
    case JourneyType.FAMILY:
      return 'Join Family';
  }
};
